import type { QueryClient } from '@tanstack/react-query';
import {
  getContentHubCollectionsSearch,
  getContentHubFavoritesSearch,
  getContentHubPaths,
  getContentHubSearch,
  type ContentHubCollection,
  type ContentHubMediaPage,
} from '@/api/beatlyApi';
import { getTokenFromStorage, logout } from '@/utils/auth';

export const CONTENT_HUB_PAGE_SIZE = 24;
export const CONTENT_HUB_COLLECTIONS_PAGE_SIZE = 50;
export const CONTENT_HUB_COLLECTIONS_MAX_PAGES = 6;

// Shared persistence options for every content-hub query: cached results survive
// the whole browser session and are re-used as fresh for up to 1 hour. After that,
// cached data still renders instantly while a silent background refetch updates it.
export const CONTENT_HUB_QUERY_PERSISTENCE = {
  gcTime: Infinity,
  staleTime: 60 * 60_000,
  refetchOnWindowFocus: false,
} as const;

export async function fetchAllCollections(
  scope: 'private' | 'team',
  token: string,
): Promise<ContentHubCollection[]> {
  let pageNumber = 0;
  const all: ContentHubCollection[] = [];
  for (let i = 0; i < CONTENT_HUB_COLLECTIONS_MAX_PAGES; i += 1) {
    const page = await getContentHubCollectionsSearch(token, {
      pageNumber,
      pageSize: CONTENT_HUB_COLLECTIONS_PAGE_SIZE,
      scope,
    });
    if (page?.isExpired) {
      logout();
      return [];
    }
    const rows = Array.isArray(page?.collections) ? page.collections : [];
    all.push(...rows);
    if (!page?.has_more) break;
    pageNumber += 1;
  }
  return all;
}

function parseCountTotal(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Warms the React Query cache with the four reads ContentHubPage issues on
 * first paint. Uses identical queryKeys to the live page so navigating to
 * /content-hub renders instantly from cache. Each prefetch is a no-op when
 * fresh data already exists.
 */
export function prefetchContentHub(queryClient: QueryClient): void {
  const token = getTokenFromStorage();
  if (!token) return;

  void queryClient.prefetchQuery({
    queryKey: ['contenthub', 'paths'],
    queryFn: async () => {
      const response = await getContentHubPaths(token);
      if (response?.isExpired) {
        logout();
        throw new Error('Token expired');
      }
      return response.folders ?? [];
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });

  void queryClient.prefetchQuery({
    queryKey: ['contenthub', 'collections'],
    queryFn: async () => {
      const [priv, team] = await Promise.all([
        fetchAllCollections('private', token),
        fetchAllCollections('team', token),
      ]);
      const byId = new Map<string, ContentHubCollection>();
      [...priv, ...team].forEach((c) => {
        if (!c?.collection_id) return;
        byId.set(c.collection_id, c);
      });
      return Array.from(byId.values()).sort((a, b) =>
        String(b.date_updated ?? '').localeCompare(String(a.date_updated ?? '')),
      );
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });

  void queryClient.prefetchQuery({
    queryKey: ['contenthub', 'favoritesCount'],
    queryFn: async (): Promise<number | null> => {
      const page = await getContentHubFavoritesSearch(token, {
        pageNumber: 0,
        pageSize: 1,
        text: null,
      });
      if (page?.isExpired) {
        logout();
        return null;
      }
      return parseCountTotal((page as { count_total?: unknown } | null)?.count_total);
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });

  void queryClient.prefetchInfiniteQuery({
    // Keep this key in sync with `galleryQueryKey` in ContentHubPageNew.tsx
    // (the 6th 'all' is the default kind filter — raw/final/all).
    queryKey: ['contenthub', 'medias', 'all', 'team', '', 'all'] as const,
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<ContentHubMediaPage> => {
      const page = await getContentHubSearch(token, {
        pageNumber: pageParam as number,
        pageSize: CONTENT_HUB_PAGE_SIZE,
        text: null,
      });
      if (page?.isExpired) {
        logout();
        throw new Error('Token expired');
      }
      return page;
    },
    getNextPageParam: (lastPage: ContentHubMediaPage, allPages: ContentHubMediaPage[]) => {
      if (!lastPage?.has_more) return undefined;
      return allPages.length;
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });
}
